import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Icon from '../../../../Assets/Icon/CustomIcon';
import { useSelector } from 'react-redux';
import Commons from '../../../../Services/Common/Common';
import { SignSection } from './SignSection';

export default function CheckList(props) {
  const sectionRefs = useRef({});
  const itemListRefs = useRef({});
  const [checkedItems, setCheckedItems] = useState({});
  const [openSectionIndex, setOpenSectionIndex] = useState(-1); // 기본값: 모두 닫힘
  const completedSections = props.completedSections || [];
  const setCompletedSections = props.setCompletedSections || (() => {});
  const { codeInfo, baseInfo } = useSelector((state) => state.permit);
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const isAuthor = Commons.DecryptoAES(userInfo.userid) === baseInfo?.userId;

  const sections = useMemo(
    () =>
      [
        { type: 'risk', name: '위험사항', items: codeInfo.risks || [] },
        { type: 'equipment', name: '보호구', items: codeInfo.equips || [] },
        {
          type: 'preparation',
          name: '준비사항',
          items: codeInfo.preparations || [],
        },
      ].filter((section) => section.items.length > 0),
    [codeInfo]
  );

  useEffect(() => {
    const initialChecked = {};

    codeInfo.risks.forEach((item) => {
      if (item.itemCheck === 'Y')
        initialChecked[`risk-${item.riskCode}`] = true;
    });
    codeInfo.equips.forEach((item) => {
      if (item.itemCheck === 'Y')
        initialChecked[`equipment-${item.workEquipCode}`] = true;
    });
    codeInfo.preparations.forEach((item) => {
      if (item.itemCheck === 'Y')
        initialChecked[`preparation-${item.preparationCode}`] = true;
    });

    setCheckedItems(initialChecked);
  }, [codeInfo]);

  // 확인 메시지 클릭 핸들러 (다음 섹션으로 이동)
  const handleSectionComplete = (currentIndex) => {
    // 현재 섹션을 완료된 것으로 표시
    setCompletedSections((prev) => {
      if (!prev.includes(currentIndex)) {
        const updated = [...prev, currentIndex];
        // 마지막 섹션의 확인 메시지를 클릭했을 때 isChecked를 true로 설정
        if (updated.length === sections.length && props.setIsChecked) {
          props.setIsChecked(true);
        }
        return updated;
      }
      return prev;
    });

    if (currentIndex < sections.length - 1) {
      // 현재 섹션 닫고 다음 섹션 열기
      setOpenSectionIndex(currentIndex + 1);
    }
  };

  // 컴포넌트 마운트 시 섹션 열기/닫기 상태 설정
  useEffect(() => {
    if (sections.length === 0) return;

    // 서명완료 또는 작성자인 경우 모든 섹션 닫기
    if (
      baseInfo?.gubun === '서명완료' ||
      baseInfo?.gubun === '승인' ||
      isAuthor
    ) {
      setOpenSectionIndex(-1);
    }
    // 요청 상태이고 작성자가 아닌 경우 첫 번째 섹션 열기
    else if (baseInfo?.gubun === '요청' && !isAuthor) {
      setOpenSectionIndex(0);
    }
    // 그 외의 경우 모두 닫기
    else {
      setOpenSectionIndex(-1);
    }
  }, [sections.length, baseInfo?.gubun, baseInfo?.userId, isAuthor]);

  // 체크 아이템 렌더링 함수
  const renderCheckItem = (item, itemKey, type) => {
    const nameMap = {
      risk: item.riskNm,
      equipment: item.workEquipNm,
      preparation: item.preparationNm,
    };

    const codeMap = {
      risk: item.riskCode,
      equipment: item.workEquipCode,
      preparation: item.preparationCode,
    };

    const isChecked = checkedItems[itemKey] ?? item.itemCheck === 'Y';
    const displayName = nameMap[type];

    return (
      <Box
        key={codeMap[type] || item.code}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          p: 2.5,
          mb: 1.5,
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '15px',
            color: isChecked ? '#111827' : '#9CA3AF',
            fontWeight: isChecked ? 'bold' : 400,
            flex: 1,
            wordBreak: 'break-word',
            pr: 1,
          }}
        >
          {displayName}
        </Typography>

        {isChecked && (
          <Box sx={{ flexShrink: 0, width: '30px', height: '30px' }}>
            <Icon.CircleCheck width={30} height={30} color={'#3a3939ff'} />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        pb: 2,
        px: 1,
      }}
    >
      {/* 작업종류 - 상단 고정 */}
      {baseInfo?.workTypeName && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            p: 2.5,
            mb: 0,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#FFFFFF',
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#3a3939ff',
            }}
          >
            작업종류
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#EA580C',
              fontSize: '16px',
              wordBreak: 'keep-all',
            }}
          >
            {baseInfo.workTypeName}
          </Typography>
        </Box>
      )}

      {/* 섹션들 (위험사항, 보호구, 준비사항) */}
      {sections.map((section, sectionIndex) => {
        const isOpen = openSectionIndex === sectionIndex;
        const isSectionConfirmed = completedSections.includes(sectionIndex);
        const itemKeyPrefix = section.type;

        return (
          <Box
            key={section.type}
            ref={(el) => {
              if (el) sectionRefs.current[`section-${sectionIndex}`] = el;
            }}
            id={`section-${sectionIndex}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {/* 섹션 헤더 - 열릴 때 고정 */}
            <Box
              onClick={() => {
                if (isOpen) {
                  setOpenSectionIndex(-1);
                } else {
                  setOpenSectionIndex(sectionIndex);
                }
              }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                p: 2,
                mb: isOpen ? 2 : 1.5,
                mt: sectionIndex === 0 ? 0 : sectionIndex > 0 ? 1 : 0,
                cursor: 'pointer',
                position: isOpen ? 'sticky' : 'relative',
                top: isOpen
                  ? baseInfo?.workTypeName
                    ? '64px'
                    : '0px'
                  : 'auto',
                zIndex: isOpen ? 9 : 1,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '17px',
                  color: '#111827',
                }}
              >
                {section.name}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {(() => {
                  // 작성자인 경우 텍스트 표시하지 않음
                  if (isAuthor) return null;

                  const gubun = baseInfo?.gubun;
                  const isSafetyManager = userInfo?.authCd === 'HEAD_MANAGER';
                  const isConfirmed =
                    gubun === '서명완료' ||
                    (gubun === '승인' && isSafetyManager) ||
                    isSectionConfirmed;

                  return (
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: isConfirmed ? '#EA580C' : '#9CA3AF',
                      }}
                    >
                      {(() => {
                        // 서명완료인 경우 항상 '항목을 모두 확인했습니다.'로 표시
                        if (gubun === '서명완료') {
                          return '항목을 모두 확인했습니다.';
                        }
                        // 승인 상태이고 안전관리자 계정인 경우 '항목을 모두 확인했습니다.'로 표시
                        if (gubun === '승인' && isSafetyManager) {
                          return '항목을 모두 확인했습니다.';
                        }
                        // 기존 로직: 섹션이 확인된 경우 (요청 상태일 때)
                        if (isSectionConfirmed) {
                          return '항목을 모두 확인했습니다.';
                        }
                        return '항목을 확인해주세요.';
                      })()}
                    </Typography>
                  );
                })()}
                {isOpen ? (
                  <ExpandLessIcon
                    sx={{
                      color: '#6B7280',
                      fontSize: '20px',
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    sx={{
                      color: '#6B7280',
                      fontSize: '20px',
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* 섹션 내용 (펼쳐진 경우만 표시) */}
            {isOpen && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  mb: 2,
                  maxHeight: baseInfo?.workTypeName
                    ? 'calc(100vh - 140px)'
                    : 'calc(100vh - 70px)',
                }}
              >
                {/* 체크리스트 항목들 - 스크롤 가능 영역 */}
                <Box
                  ref={(el) => {
                    if (el) itemListRefs.current[`list-${sectionIndex}`] = el;
                  }}
                  sx={{
                    maxHeight: baseInfo?.workTypeName
                      ? 'calc(100vh - 200px)'
                      : 'calc(100vh - 130px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#D1D5DB',
                      borderRadius: '3px',
                    },
                  }}
                >
                  {section.items.map((item) => {
                    const itemKey = `${itemKeyPrefix}-${item.riskCode || item.workEquipCode || item.preparationCode}`;
                    return renderCheckItem(item, itemKey, section.type);
                  })}
                </Box>

                {/* 하단 확인 메시지 - 고정 (스크롤 영역 밖) - 요청일 때만 표시 */}
                {baseInfo?.gubun === '요청' && (
                  <Box
                    onClick={() => handleSectionComplete(sectionIndex)}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      p: 2,
                      mt: 1,
                      borderRadius: '12px',
                      backgroundColor: '#F3F4F6',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: '#E5E7EB',
                      },
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        color: '#9CA3AF',
                        fontSize: '20px',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        color: isSectionConfirmed ? '#2563EB' : '#6B7280',
                        fontWeight: 500,
                        fontSize: '15px',
                      }}
                    >
                      {section.name} 항목을 모두 확인했습니다.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {/* 작업관리 부서 서명 */}
      <SignSection title="작업관리 부서" approvers={baseInfo?.approverList} />

      {/* 허가관리 부서 서명  */}
      {baseInfo?.safetyApprovalStatus === 'Y' && (
        <SignSection
          title="허가관리 부서"
          approvers={[
            {
              approverName: baseInfo.safetyApproverName,
              approverPosition: baseInfo.safetyApproverPosition,
              approverDepNm: baseInfo.depNmLast,
              signaturePath: baseInfo.safetySignaturePath,
            },
          ]}
        />
      )}
    </Box>
  );
}
